(() => {
  const formatMoney = (cents, format) => {
    const value = Number(cents || 0);
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match?.[1] || 'amount';
    const decimals = placeholder.includes('no_decimals') ? 0 : 2;
    const decimalMark = placeholder.includes('comma_separator') ? ',' : '.';
    const thousands = placeholder.includes('comma_separator') ? '.' : ',';
    const number = (value / 100).toFixed(decimals);
    const [whole, fraction] = number.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const output = fraction ? `${grouped}${decimalMark}${fraction}` : grouped;
    return template.replace(match?.[0] || '{{amount}}', output);
  };

  class RoadRexProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('.rr-pdp__form');
      this.variantInput = this.querySelector('[data-rr-variant-id]');
      this.options = [...this.querySelectorAll('[data-rr-product-option]')];
      this.quantity = this.querySelector('[data-rr-quantity]');
      this.pickupContainer = this.querySelector('[data-rr-pickup-availability]');
      this.primaryPurchase = this.querySelector('[data-rr-primary-purchase]');
      this.mobileBuy = this.querySelector('[data-rr-mobile-buy]');
      this.variants = this.readVariants();
      this.quantityRules = this.readQuantityRules();
      this.form?.addEventListener('submit', (event) => this.validateSubmission(event));

      const initialVariant = this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value));
      if (initialVariant) {
        this.updateEnquiryContext(initialVariant);
        this.updateQuantityRule(initialVariant);
      }

      this.options.forEach((select) => select.addEventListener('change', () => this.updateVariant()));
      this.querySelector('[data-rr-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
      this.querySelector('[data-rr-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
      this.quantity?.addEventListener('change', () => this.updateEnquiryContext(this.currentVariant()));
      this.querySelectorAll('[data-rr-media-trigger]').forEach((button) => {
        button.addEventListener('click', () => this.activateMedia(String(button.dataset.rrMediaTrigger)));
      });
      this.setupMobileBuy();
    }

    disconnectedCallback() {
      this.pickupController?.abort();
      this.mobileBuyObserver?.disconnect();
    }

    setupMobileBuy() {
      if (!this.mobileBuy || !this.primaryPurchase) return;

      const setVisibility = (visible) => {
        this.mobileBuy.hidden = !visible;
        this.mobileBuy.classList.toggle('is-visible', visible);
        this.mobileBuy.setAttribute('aria-hidden', String(!visible));
      };

      if (!('IntersectionObserver' in window)) {
        setVisibility(true);
        return;
      }

      this.mobileBuyObserver = new IntersectionObserver(([entry]) => {
        const purchaseHasPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        setVisibility(purchaseHasPassed);
      }, { threshold: 0 });
      this.mobileBuyObserver.observe(this.primaryPurchase);
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-rr-product-variants]')?.textContent || '[]');
      } catch (_error) {
        return [];
      }
    }

    readQuantityRules() {
      try {
        return JSON.parse(this.querySelector('[data-rr-quantity-rules]')?.textContent || '{}');
      } catch (_error) {
        return {};
      }
    }

    currentVariant() {
      return this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value));
    }

    validateSubmission(event) {
      if (this.dataset.purchaseMode === 'quote' || this.dataset.purchaseMode === 'inquiry') return;
      const variant = this.currentVariant();
      if (variant?.available) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.querySelector('[data-rr-unavailable-enquiry]:not([hidden])')?.focus();
    }

    changeQuantity(change) {
      if (!this.quantity) return;
      const minimum = Number(this.quantity.min || 1);
      const maximum = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const increment = Math.max(1, Number(this.quantity.step) || 1);
      const current = Number(this.quantity.value || minimum);
      this.quantity.value = String(Math.max(minimum, Math.min(maximum, current + (change * increment))));
      this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateQuantityRule(variant) {
      if (!this.quantity || !variant?.id) return;
      const rule = this.quantityRules?.[String(variant.id)] || variant.quantity_rule || {};
      const minimum = Math.max(1, Number(rule.min) || 1);
      const increment = Math.max(1, Number(rule.increment) || 1);
      const maximum = Number(rule.max) > 0 ? Number(rule.max) : null;
      this.quantity.min = String(minimum);
      this.quantity.step = String(increment);
      if (maximum) this.quantity.max = String(maximum);
      else this.quantity.removeAttribute('max');
      let value = Number(this.quantity.value) || minimum;
      value = Math.max(minimum, maximum ? Math.min(maximum, value) : value);
      value = minimum + Math.round((value - minimum) / increment) * increment;
      if (maximum) value = Math.min(maximum, value);
      this.quantity.value = String(value);
    }

    updateVariant() {
      const selected = this.options.map((select) => select.value);
      const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
      if (!variant) {
        if (this.variantInput) {
          this.variantInput.value = '';
          this.variantInput.disabled = true;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.updatePrice(null);
        this.updateAvailability(null);
        this.updateEnquiryContext(null);
        if (this.pickupContainer) this.pickupContainer.hidden = true;
        this.dispatchEvent(new CustomEvent('rr:variant-change', { bubbles: true, detail: { variant: null } }));
        return;
      }

      if (this.variantInput) {
        this.variantInput.disabled = false;
        this.variantInput.value = variant.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateQuantityRule(variant);
      this.updateEnquiryContext(variant);
      this.updatePickupAvailability(variant);
      this.querySelectorAll('[data-rr-sku]').forEach((node) => { node.textContent = variant.sku || '—'; });
      if (variant.featured_media?.id) this.activateMedia(String(variant.featured_media.id));

      if (window.history?.replaceState && this.dataset.productUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.dispatchEvent(new CustomEvent('rr:variant-change', { bubbles: true, detail: { variant } }));
    }

    updatePrice(variant) {
      if (!variant) {
        this.querySelectorAll('[data-rr-product-price]').forEach((node) => {
          node.textContent = this.dataset.invalidVariantLabel || 'Unavailable combination';
        });
        this.querySelectorAll('[data-rr-product-compare], [data-rr-saving]').forEach((node) => {
          node.hidden = true;
          node.textContent = '';
        });
        this.querySelectorAll('[data-rr-gallery-sale]').forEach((node) => { node.hidden = true; });
        return;
      }
      this.querySelectorAll('[data-rr-product-price]').forEach((node) => {
        node.textContent = formatMoney(variant.price, this.dataset.moneyFormat);
      });
      this.querySelectorAll('[data-rr-product-compare]').forEach((node) => {
        const visible = Number(variant.compare_at_price) > Number(variant.price);
        node.hidden = !visible;
        node.textContent = visible ? formatMoney(variant.compare_at_price, this.dataset.moneyFormat) : '';
      });
      this.querySelectorAll('[data-rr-saving]').forEach((node) => {
        const saving = Number(variant.compare_at_price || 0) - Number(variant.price || 0);
        node.hidden = saving <= 0;
        node.textContent = saving > 0 ? `${this.dataset.savingLabel || 'Save'} ${formatMoney(saving, this.dataset.moneyFormat)}` : '';
      });
      this.querySelectorAll('[data-rr-gallery-sale]').forEach((node) => {
        node.hidden = Number(variant.compare_at_price || 0) <= Number(variant.price || 0);
      });
    }

    updateAvailability(variant) {
      const available = Boolean(variant?.available);
      this.querySelectorAll('[data-rr-add-button]').forEach((button) => {
        button.disabled = !available;
        button.hidden = !available;
        const label = button.querySelector('[data-rr-add-label]');
        if (label) label.textContent = available ? button.dataset.addLabel : button.dataset.soldOutLabel;
      });
      this.querySelectorAll('[data-rr-unavailable-enquiry]').forEach((button) => {
        button.hidden = available;
      });
      this.querySelectorAll('[data-rr-dynamic-checkout]').forEach((checkout) => {
        checkout.hidden = !available;
      });
      const stock = this.querySelector('[data-rr-stock]');
      const isInquiry = this.dataset.purchaseMode === 'inquiry';
      stock?.classList.toggle('is-enquiry', isInquiry);
      stock?.classList.toggle('is-available', available && !isInquiry);
      const stockLabel = this.querySelector('[data-rr-stock-label]');
      if (stockLabel) {
        if (this.dataset.purchaseMode === 'inquiry') stockLabel.textContent = this.dataset.inquiryLabel || 'Product enquiry required';
        else if (!available) stockLabel.textContent = this.dataset.unavailableLabel || 'Currently unavailable';
        else if (this.dataset.purchaseMode === 'quote') stockLabel.textContent = this.dataset.freightLabel || 'Freight quote required';
        else if (this.dataset.purchaseMode === 'pickup') stockLabel.textContent = this.dataset.pickupLabel || 'Canberra pickup only';
        else stockLabel.textContent = this.dataset.availableLabel || 'Available to order';
      }
    }

    async updatePickupAvailability(variant) {
      const container = this.pickupContainer || this.querySelector('[data-rr-pickup-availability]');
      if (!container || !variant?.id || !this.dataset.productUrl || !this.dataset.sectionId) return;

      this.pickupController?.abort();
      const controller = new AbortController();
      this.pickupController = controller;
      container.classList.add('is-loading');
      container.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Pickup availability request failed (${response.status})`);

        const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = fragment.querySelector('[data-rr-pickup-availability]');
        if (!replacement) {
          container.hidden = true;
          return;
        }

        container.replaceWith(replacement);
        this.pickupContainer = replacement;
      } catch (error) {
        if (error.name !== 'AbortError') container.hidden = true;
      } finally {
        if (this.pickupController === controller) {
          this.pickupContainer?.classList.remove('is-loading');
          this.pickupContainer?.removeAttribute('aria-busy');
        }
      }
    }

    updateEnquiryContext(variant) {
      const parts = [this.dataset.productTitle || 'Product'];
      if (variant?.title && variant.title !== 'Default Title') parts.push(variant.title);
      if (variant?.sku) parts.push(`SKU ${variant.sku}`);
      const quantity = Math.max(1, Number(this.quantity?.value) || 1);
      if (quantity > 1 || ['quote', 'inquiry', 'cart_inquiry'].includes(this.dataset.purchaseMode)) parts.push(`Quantity ${quantity}`);
      const label = parts.join(' — ');
      this.querySelectorAll('[data-rr-enquiry-open][data-product-title]').forEach((button) => {
        button.dataset.productTitle = label;
      });
    }

    activateMedia(id) {
      this.querySelectorAll('[data-rr-media-id]').forEach((media) => { media.hidden = String(media.dataset.rrMediaId) !== id; });
      this.querySelectorAll('[data-rr-media-trigger]').forEach((button) => {
        const active = String(button.dataset.rrMediaTrigger) === id;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        if (active) {
          const status = this.querySelector('[data-rr-media-status]');
          const total = this.querySelectorAll('[data-rr-media-trigger]').length;
          if (status) status.textContent = `Product media ${button.dataset.rrMediaPosition || ''} of ${total} selected.`;
        }
      });
    }
  }

  class RoadRexRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      if (this.initialized) return;
      this.initialized = true;
      this.retryButton = this.querySelector('[data-rr-recommendations-retry]');
      this.boundRetry = () => this.load();
      this.retryButton?.addEventListener('click', this.boundRetry);
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.retryButton?.removeEventListener('click', this.boundRetry);
    }

    async load() {
      if (this.loading || !this.dataset.url) return;
      this.loading = true;
      this.hidden = false;
      this.setLoadingState();
      this.controller?.abort();
      this.controller = new AbortController();

      try {
        const response = await fetch(this.dataset.url, {
          headers: { Accept: 'text/html' },
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = [...documentFragment.querySelectorAll('[data-rr-recommendations]')]
          .find((node) => node.dataset.sectionId === this.dataset.sectionId);
        if (!replacement) throw new Error('Recommendations section was missing from the response');

        this.dataset.loaded = 'true';
        if (replacement.dataset.empty === 'true') {
          this.showEmptyState();
          return;
        }

        this.innerHTML = replacement.innerHTML;
        this.hidden = false;
        this.initializeInjectedCards();
        this.dispatchEvent(new CustomEvent('roadrex:recommendations:loaded', { bubbles: true }));
      } catch (error) {
        if (error.name !== 'AbortError') this.showErrorState();
      } finally {
        this.loading = false;
      }
    }

    setLoadingState() {
      const status = this.querySelector('[data-rr-recommendations-status]');
      const message = this.querySelector('[data-rr-recommendations-message]');
      status?.classList.remove('has-error', 'is-empty');
      if (message && message.dataset.loadingLabel) message.textContent = message.dataset.loadingLabel;
      this.querySelector('.rr-recommendations__skeletons')?.removeAttribute('hidden');
      this.querySelector('.rr-recommendations__spinner')?.removeAttribute('hidden');
      if (this.retryButton) this.retryButton.hidden = true;
    }

    showEmptyState() {
      if (this.dataset.designMode !== 'true') {
        this.hidden = true;
        return;
      }
      const status = this.querySelector('[data-rr-recommendations-status]');
      const message = this.querySelector('[data-rr-recommendations-message]');
      status?.classList.add('is-empty');
      if (message) message.textContent = this.dataset.emptyLabel || 'No product recommendations are available yet.';
      this.querySelector('.rr-recommendations__skeletons')?.remove();
      this.querySelector('.rr-recommendations__spinner')?.remove();
      if (this.retryButton) this.retryButton.hidden = true;
    }

    showErrorState() {
      const status = this.querySelector('[data-rr-recommendations-status]');
      const message = this.querySelector('[data-rr-recommendations-message]');
      status?.classList.add('has-error');
      if (message) message.textContent = this.dataset.errorLabel || 'Recommendations could not be loaded.';
      this.querySelector('.rr-recommendations__skeletons')?.setAttribute('hidden', '');
      this.querySelector('.rr-recommendations__spinner')?.setAttribute('hidden', '');
      if (this.retryButton) this.retryButton.hidden = false;
    }

    initializeInjectedCards() {
      document.dispatchEvent(new CustomEvent('rr:cards:ready', { detail: { root: this } }));
    }
  }

  const initializeProductInformation = () => {
    document.querySelectorAll('.rr-product-details').forEach((section) => {
      if (section.dataset.rrDisclosuresReady === 'true') return;
      section.dataset.rrDisclosuresReady = 'true';
      const disclosures = [...section.querySelectorAll('.rr-product-details__overview, .rr-product-details__disclosure')];
      disclosures.forEach((disclosure) => {
        disclosure.addEventListener('toggle', () => {
          if (!disclosure.open) return;
          disclosures.forEach((other) => {
            if (other !== disclosure) other.open = false;
          });
        });
      });
    });
  };

  if (!customElements.get('road-rex-product')) customElements.define('road-rex-product', RoadRexProduct);
  if (!customElements.get('road-rex-recommendations')) customElements.define('road-rex-recommendations', RoadRexRecommendations);
  initializeProductInformation();
  document.addEventListener('shopify:section:load', initializeProductInformation);
})();
